/**
 * PublishingTab — per-site publishing policy configuration.
 *
 * Allows operators to create, edit, pause/resume, and inspect
 * a per-site publishing policy (quota, schedule, network scope, category scope).
 *
 * Inspect card shows ONLY API-provided fields.
 * Phase 8 will add run history, quota-remaining, and next-run.
 */
import { useState, useEffect } from "react";
import {
  CalendarClock,
  Clock,
  Save,
  CheckCircle2,
  X,
  Plus,
} from "lucide-react";
import { publishingPolicies } from "../lib/api.js";
import type { PublishPolicy, PublishPolicyBody } from "../lib/api.js";

// ─── Timezone helpers ─────────────────────────────────────────

const TIMEZONE_FALLBACK = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Anchorage",
  "Pacific/Honolulu",
  "Europe/London",
  "Europe/Paris",
  "Europe/Berlin",
  "Europe/Moscow",
  "Asia/Dubai",
  "Asia/Kolkata",
  "Asia/Shanghai",
  "Asia/Tokyo",
  "Australia/Sydney",
  "Pacific/Auckland",
];

function getTimezones(): string[] {
  try {
    const fn = (Intl as unknown as { supportedValuesOf?: (key: string) => string[] })
      .supportedValuesOf;
    if (typeof fn === "function") return fn("timeZone");
  } catch {
    // fall through
  }
  return TIMEZONE_FALLBACK;
}

const TIMEZONES = getTimezones();

// ─── Shared style tokens (mirror SiteAffiliatesTab) ───────────

const inputCls =
  "w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-brand-500";
const labelCls = "block text-xs font-medium text-gray-400 mb-1.5";
const selectCls = inputCls;

function SectionHeader({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <div className="pb-3 border-b border-gray-800 mb-4">
      <p className="text-sm font-semibold text-gray-100">{title}</p>
      {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
    </div>
  );
}

// ─── Category chip input ──────────────────────────────────────

function CategoryChips({
  values,
  onChange,
}: {
  values: string[];
  onChange: (next: string[]) => void;
}) {
  const [input, setInput] = useState("");

  function addChip() {
    const v = input.trim();
    if (!v) return;
    if (!values.includes(v)) onChange([...values, v]);
    setInput("");
  }

  return (
    <div>
      <label className={labelCls}>Category Scope</label>
      <div className="flex gap-2 mb-2">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addChip();
            }
          }}
          placeholder="e.g. electronics"
          className="flex-1 px-3 py-1.5 bg-gray-800 border border-gray-700 rounded-lg text-xs text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-brand-500"
        />
        <button
          type="button"
          onClick={addChip}
          className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-gray-200 text-xs rounded-lg transition-colors"
        >
          <Plus className="w-3.5 h-3.5" />
        </button>
      </div>
      {values.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {values.map((v) => (
            <span
              key={v}
              className="flex items-center gap-1 px-2 py-1 bg-gray-800 border border-gray-700 rounded-full text-xs text-gray-300"
            >
              {v}
              <button
                onClick={() => onChange(values.filter((x) => x !== v))}
                className="text-gray-600 hover:text-red-400 transition-colors"
                aria-label={`Remove ${v}`}
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      {values.length === 0 && (
        <p className="text-xs text-gray-600 italic">
          No categories — leave empty to publish across all categories.
        </p>
      )}
    </div>
  );
}

// ─── Inspect card ─────────────────────────────────────────────

function InspectCard({ policy }: { policy: PublishPolicy }) {
  const statusLabel = policy.enabled ? "Active" : "Paused";
  const statusCls = policy.enabled
    ? "bg-green-500/10 text-green-400"
    : "bg-gray-500/10 text-gray-400";

  return (
    <div className="bg-[var(--bg-secondary,theme(colors.gray.900))]/60 border border-gray-800 rounded-xl p-4 space-y-3">
      <SectionHeader
        title="Policy Status"
        subtitle="Fields shown are exactly as returned by the server."
      />

      <div className="grid grid-cols-2 gap-x-6 gap-y-2.5 text-xs">
        <div>
          <p className={labelCls + " mb-0.5"}>Status</p>
          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${statusCls}`}>
            {statusLabel}
          </span>
        </div>

        <div>
          <p className={labelCls + " mb-0.5"}>Daily Quota</p>
          <p className="text-gray-100 font-medium">{policy.dailyQuota} pages/day</p>
        </div>

        <div>
          <p className={labelCls + " mb-0.5"}>Run Time (local)</p>
          <p className="text-gray-100">{policy.runTimeLocal}</p>
        </div>

        <div>
          <p className={labelCls + " mb-0.5"}>Timezone</p>
          <p className="text-gray-100">{policy.timezone}</p>
        </div>

        <div>
          <p className={labelCls + " mb-0.5"}>Network Scope</p>
          <p className="text-gray-100">
            {policy.networks.length > 0 ? policy.networks.join(", ") : "All networks"}
          </p>
        </div>

        <div>
          <p className={labelCls + " mb-0.5"}>Category Scope</p>
          <p className="text-gray-100">
            {policy.categories.length > 0 ? policy.categories.join(", ") : "All categories"}
          </p>
        </div>

        {policy.lastRunDate && (
          <div className="col-span-2">
            <p className={labelCls + " mb-0.5"}>Last Run</p>
            <p className="text-gray-100">{new Date(policy.lastRunDate).toLocaleString()}</p>
          </div>
        )}
      </div>

      <p className="text-[10px] text-gray-600 pt-1 border-t border-gray-800">
        Run history, quota-remaining, and next-run counters arrive in Phase 8.
      </p>
    </div>
  );
}

// ─── Main PublishingTab component ────────────────────────────

export interface PublishingTabProps {
  siteId: string;
  tenantCap: number;
  initialPolicy?: PublishPolicy | null;
}

export function PublishingTab({ siteId, tenantCap, initialPolicy }: PublishingTabProps) {
  const [policy, setPolicy] = useState<PublishPolicy | null>(
    initialPolicy !== undefined ? (initialPolicy ?? null) : null
  );
  const [loading, setLoading] = useState(initialPolicy === undefined);
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Form state
  const [dailyQuota, setDailyQuota] = useState<number>(policy?.dailyQuota ?? 10);
  const [runTimeLocal, setRunTimeLocal] = useState<string>(policy?.runTimeLocal ?? "09:00");
  const [timezone, setTimezone] = useState<string>(
    policy?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone ?? "America/New_York"
  );
  const [networks, setNetworks] = useState<("amazon" | "ebay")[]>(policy?.networks ?? []);
  const [categories, setCategories] = useState<string[]>(policy?.categories ?? []);

  const quotaExceeded = dailyQuota > tenantCap;
  const quotaErrorId = "quota-error";

  // Load on mount (skip when initialPolicy provided — deterministic Storybook mode)
  useEffect(() => {
    if (initialPolicy !== undefined) return;
    void loadPolicy();
  }, [siteId]);

  async function loadPolicy() {
    setLoading(true);
    setErr(null);
    try {
      const p = await publishingPolicies.getBySite(siteId);
      setPolicy(p);
      if (p) seedForm(p);
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : "Failed to load policy");
    } finally {
      setLoading(false);
    }
  }

  function seedForm(p: PublishPolicy) {
    setDailyQuota(p.dailyQuota);
    setRunTimeLocal(p.runTimeLocal);
    setTimezone(p.timezone);
    setNetworks(p.networks);
    setCategories(p.categories);
  }

  async function handleSave() {
    if (quotaExceeded) return;
    setSaving(true);
    setErr(null);

    const body: PublishPolicyBody = {
      dailyQuota,
      runTimeLocal,
      timezone,
      networks,
      categories,
    };

    try {
      let updated: PublishPolicy;
      if (policy?._id) {
        updated = await publishingPolicies.update(policy._id, body);
      } else {
        updated = await publishingPolicies.create({ ...body, siteId });
      }
      setPolicy(updated);
      seedForm(updated);
      setSavedMsg(true);
      setTimeout(() => setSavedMsg(false), 2500);
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : "Save failed");
    } finally {
      setSaving(false);
    }
  }

  async function handleToggleEnabled() {
    if (!policy?._id) return;
    setErr(null);
    try {
      const updated = policy.enabled
        ? await publishingPolicies.pause(policy._id)
        : await publishingPolicies.resume(policy._id);
      setPolicy(updated);
      seedForm(updated);
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : "Toggle failed");
    }
  }

  function toggleNetwork(n: "amazon" | "ebay") {
    setNetworks((prev) =>
      prev.includes(n) ? prev.filter((x) => x !== n) : [...prev, n]
    );
  }

  // ─── Loading state ──────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-5 h-5 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // ─── Render ─────────────────────────────────────────────────

  return (
    <div className="space-y-6 max-w-2xl">
      {/* Save bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CalendarClock className="w-4 h-4 text-gray-400" />
          <h2 className="text-sm font-semibold text-gray-100">Publishing Policy</h2>
        </div>
        <div className="flex items-center gap-2">
          {savedMsg && (
            <span className="text-xs text-green-400 flex items-center gap-1">
              <CheckCircle2 className="w-3.5 h-3.5" />
              Saved
            </span>
          )}
          {err && <span className="text-xs text-red-400">{err}</span>}
          <button
            onClick={() => void handleSave()}
            disabled={saving || quotaExceeded}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-xs font-medium rounded-lg transition-colors"
          >
            <Save className="w-3.5 h-3.5" />
            {saving ? "Saving…" : policy ? "Save Changes" : "Create Policy"}
          </button>
        </div>
      </div>

      {/* Pause / resume toggle (only when policy exists) */}
      {policy && (
        <div className="flex items-center justify-between px-4 py-3 bg-gray-800/40 border border-gray-700 rounded-xl">
          <div>
            <p className="text-sm font-medium text-gray-100">
              {policy.enabled ? "Active" : "Paused"}
            </p>
            <p className="text-xs text-gray-500 mt-0.5">
              {policy.enabled
                ? "Publishing runs on schedule."
                : "Publishing is paused. Quota and settings are preserved."}
            </p>
          </div>
          <button
            role="switch"
            aria-checked={policy.enabled}
            onClick={() => void handleToggleEnabled()}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-gray-900 ${
              policy.enabled ? "bg-indigo-600" : "bg-gray-700"
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                policy.enabled ? "translate-x-6" : "translate-x-1"
              }`}
            />
          </button>
        </div>
      )}

      {/* Empty state */}
      {!policy && (
        <div className="flex flex-col items-center justify-center py-10 border border-dashed border-gray-700 rounded-xl text-center gap-3">
          <CalendarClock className="w-10 h-10 text-gray-600" />
          <div>
            <p className="text-sm font-medium text-gray-200">No publishing policy yet.</p>
            <p className="text-xs text-gray-500 mt-1">
              Configure a schedule below and click "Create Policy" to get started.
            </p>
          </div>
        </div>
      )}

      {/* Configuration form */}
      <div className="space-y-5">
        <SectionHeader
          title="Policy Configuration"
          subtitle="Define how many pages to publish per day and when to run."
        />

        {/* Daily quota */}
        <div>
          <label htmlFor="daily-quota" className={labelCls}>
            Daily Quota
          </label>
          <input
            id="daily-quota"
            type="number"
            min={1}
            max={tenantCap}
            value={dailyQuota}
            onChange={(e) => setDailyQuota(Number(e.target.value))}
            aria-invalid={quotaExceeded}
            aria-describedby={quotaExceeded ? quotaErrorId : undefined}
            className={`${inputCls} ${quotaExceeded ? "border-red-500 focus:ring-red-500" : ""}`}
          />
          {quotaExceeded && (
            <p id={quotaErrorId} className="mt-1 text-xs text-red-400">
              Exceeds your plan limit of {tenantCap}. Contact support to raise it.
            </p>
          )}
          {!quotaExceeded && (
            <p className="text-[10px] text-gray-600 mt-1">
              Max {tenantCap} pages/day on your current plan. Server enforces this limit.
            </p>
          )}
        </div>

        {/* Run time */}
        <div>
          <label htmlFor="run-time-local" className={labelCls}>
            <span className="flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5" />
              Run Time (local)
            </span>
          </label>
          <input
            id="run-time-local"
            type="time"
            value={runTimeLocal}
            onChange={(e) => setRunTimeLocal(e.target.value)}
            className={inputCls}
          />
        </div>

        {/* Timezone */}
        <div>
          <label htmlFor="timezone" className={labelCls}>
            Timezone
          </label>
          <select
            id="timezone"
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
            className={selectCls}
          >
            {TIMEZONES.map((tz) => (
              <option key={tz} value={tz}>
                {tz}
              </option>
            ))}
          </select>
        </div>

        {/* Network scope */}
        <div>
          <p className={labelCls}>Network Scope</p>
          <div className="space-y-2">
            {(["amazon", "ebay"] as const).map((n) => (
              <label key={n} className="flex items-center gap-2.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={networks.includes(n)}
                  onChange={() => toggleNetwork(n)}
                  className="w-3.5 h-3.5 rounded border-gray-600 bg-gray-800 accent-indigo-500"
                />
                <span className="text-xs text-gray-200 capitalize">{n}</span>
              </label>
            ))}
          </div>
          <p className="text-[10px] text-gray-600 mt-1.5">
            Leave both unchecked to include all supported networks.
          </p>
        </div>

        {/* Category chips */}
        <CategoryChips values={categories} onChange={setCategories} />
      </div>

      {/* Inspect card — shown when policy exists */}
      {policy && <InspectCard policy={policy} />}
    </div>
  );
}
